import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle, Clock, PhoneCall, MessageSquare, Ticket, Settings, AlertTriangle, Zap, ShieldAlert } from 'lucide-react';
import {
  useActiveEscalations,
  useEscalationMetrics,
  useEscalationConfig,
  saveEscalationConfig,
  resolveEscalation,
  useAutonomousConfig,
  saveAutonomousConfig,
  type EscalationConfig,
  type ActiveEscalation,
  type AutonomousResponseConfig,
} from '../../hooks/useSecOps';

const LEVEL_COLORS: Record<string, string> = {
  P1: 'bg-red-100 text-red-700',
  P2: 'bg-orange-100 text-orange-700',
  P3: 'bg-yellow-100 text-yellow-700',
  P4: 'bg-blue-100 text-blue-700',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-red-100 text-red-700',
  acknowledged: 'bg-yellow-100 text-yellow-700',
  resolved: 'bg-green-100 text-green-700',
};

const LEVEL_ICONS: Record<string, typeof PhoneCall> = {
  P1: PhoneCall,
  P2: MessageSquare,
  P3: Bell,
  P4: Ticket,
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>
      {value}
    </span>
  );
}

function MetricCard({ label, value, sub, color = 'text-gray-900' }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function ActiveEscalationRow({ esc, onResolve }: { esc: ActiveEscalation; onResolve: (id: string) => void }) {
  const navigate = useNavigate();
  const LevelIcon = LEVEL_ICONS[esc.escalation_level] ?? Bell;
  const minutesAgo = Math.round((Date.now() - new Date(esc.triggered_at).getTime()) / 60000);

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <LevelIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <Badge value={esc.escalation_level} colors={LEVEL_COLORS} />
        </div>
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => navigate(`/secops/incidents/${esc.incident_id}`)}
          className="text-sm font-medium text-indigo-600 hover:underline text-left"
        >
          {esc.incident_title}
        </button>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <Badge value={esc.status} colors={STATUS_COLORS} />
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
        {minutesAgo}m ago
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
        {esc.psa_ticket_id ? (
          <span className="inline-flex items-center gap-1 text-green-600">
            <Ticket className="w-3 h-3" /> Created
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-right">
        {esc.status !== 'resolved' && (
          <button
            onClick={() => onResolve(esc.id)}
            className="text-xs text-green-600 hover:text-green-800 font-medium"
          >
            Mark Resolved
          </button>
        )}
      </td>
    </tr>
  );
}

function ConfigPanel({ config, onSave }: { config: EscalationConfig; onSave: (c: Partial<EscalationConfig>) => Promise<void> }) {
  const [form, setForm] = useState<EscalationConfig>({ ...config });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-5">
      <div className="flex items-center gap-2 mb-2">
        <Settings className="w-5 h-5 text-gray-500" />
        <h3 className="font-semibold text-gray-900">Escalation Configuration</h3>
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.auto_escalate}
            onChange={e => setForm(f => ({ ...f, auto_escalate: e.target.checked }))}
            className="w-4 h-4 text-indigo-600 rounded"
          />
          <span className="text-sm font-medium text-gray-700">Auto-escalate P1/P2 incidents</span>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">P1 Delay (minutes, 0 = immediate)</label>
          <input
            type="number"
            min={0}
            max={60}
            value={form.p1_delay_minutes}
            onChange={e => setForm(f => ({ ...f, p1_delay_minutes: parseInt(e.target.value) || 0 }))}
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">P2 Delay (minutes)</label>
          <input
            type="number"
            min={0}
            max={120}
            value={form.p2_delay_minutes}
            onChange={e => setForm(f => ({ ...f, p2_delay_minutes: parseInt(e.target.value) || 0 }))}
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">PSA Board ID</label>
          <input
            type="text"
            value={form.psa_board_id}
            onChange={e => setForm(f => ({ ...f, psa_board_id: e.target.value }))}
            placeholder="security"
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">On-Call Schedule ID</label>
          <input
            type="text"
            value={form.oncall_schedule_id}
            onChange={e => setForm(f => ({ ...f, oncall_schedule_id: e.target.value }))}
            placeholder="schedule-id"
            className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Slack Webhook URL (optional)</label>
        <input
          type="url"
          value={form.slack_webhook_url ?? ''}
          onChange={e => setForm(f => ({ ...f, slack_webhook_url: e.target.value || undefined }))}
          placeholder="https://hooks.slack.com/..."
          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Notification Emails (comma-separated)</label>
        <input
          type="text"
          value={form.notify_emails.join(', ')}
          onChange={e => setForm(f => ({ ...f, notify_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
          placeholder="soc@example.com, ciso@example.com"
          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.psa_create_tickets}
            onChange={e => setForm(f => ({ ...f, psa_create_tickets: e.target.checked }))}
            className="w-4 h-4 text-indigo-600 rounded"
          />
          <span className="text-sm font-medium text-gray-700">Auto-create PSA tickets for P1/P2</span>
        </label>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" /> Saved
          </span>
        )}
      </div>
    </div>
  );
}

function AutonomousResponsePanel({
  config,
  onSave,
}: {
  config: AutonomousResponseConfig;
  onSave: (c: Partial<AutonomousResponseConfig>) => Promise<void>;
}) {
  const [form, setForm] = useState<AutonomousResponseConfig>({ ...config });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-amber-500" />
          <h3 className="font-semibold text-gray-900">Autonomous Response Mode</h3>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
            form.autonomous_response_enabled
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-500'
          }`}
        >
          <ShieldAlert className="w-3.5 h-3.5" />
          {form.autonomous_response_enabled ? 'AUTONOMOUS' : 'MANUAL'}
        </span>
      </div>

      {/* Warning banner */}
      <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800">
            When enabled, SOC will automatically execute matching playbooks for critical/high incidents without human approval.
            All autonomous actions are fully audit-logged with timeline entries.
          </p>
        </div>
      </div>

      {/* Master toggle */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.autonomous_response_enabled}
            onChange={e => setForm(f => ({ ...f, autonomous_response_enabled: e.target.checked }))}
            className="w-4 h-4 text-amber-600 rounded"
          />
          <span className="text-sm font-medium text-gray-700">Enable autonomous response</span>
        </label>
      </div>

      {/* Severity threshold */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Minimum severity threshold</label>
        <select
          value={form.autonomous_severity_threshold}
          onChange={e => setForm(f => ({ ...f, autonomous_severity_threshold: e.target.value as 'critical' | 'high' }))}
          disabled={!form.autonomous_response_enabled}
          className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50 disabled:bg-gray-50"
        >
          <option value="critical">Critical only</option>
          <option value="high">High and above</option>
        </select>
        <p className="text-xs text-gray-400 mt-1">
          {form.autonomous_severity_threshold === 'critical'
            ? 'Only critical severity incidents will trigger autonomous playbook execution.'
            : 'Both high and critical severity incidents will trigger autonomous playbook execution.'}
        </p>
      </div>

      {/* Backup verification */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.autonomous_require_backup_verify}
            onChange={e => setForm(f => ({ ...f, autonomous_require_backup_verify: e.target.checked }))}
            disabled={!form.autonomous_response_enabled}
            className="w-4 h-4 text-amber-600 rounded disabled:opacity-50"
          />
          <span className="text-sm font-medium text-gray-700">Require backup verification before isolation</span>
        </label>
      </div>
      <p className="text-xs text-gray-400 -mt-3 ml-6">
        When enabled, a backup verification step is prepended to any autonomous playbook that contains device isolation.
      </p>

      {/* Save */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Autonomous Config'}
        </button>
        {saved && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" /> Saved
          </span>
        )}
      </div>
    </div>
  );
}

export default function EscalationPage() {
  const { data: activeData, loading: activeLoading, refresh: refreshActive } = useActiveEscalations();
  const { data: metricsData, loading: metricsLoading } = useEscalationMetrics();
  const { data: configData, loading: configLoading } = useEscalationConfig();
  const { data: autonomousData, loading: autonomousLoading } = useAutonomousConfig();

  const active = (activeData?.data ?? []) as ActiveEscalation[];
  const metrics = metricsData?.data;
  const config = configData?.data;
  const autonomousConfig = autonomousData?.data;

  async function handleResolve(id: string) {
    try {
      await resolveEscalation(id);
      refreshActive();
    } catch {
      // ignore
    }
  }

  async function handleSaveConfig(updates: Partial<EscalationConfig>) {
    await saveEscalationConfig(updates);
  }

  async function handleSaveAutonomousConfig(updates: Partial<AutonomousResponseConfig>) {
    await saveAutonomousConfig(updates);
  }

  const loading = activeLoading || metricsLoading;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Escalation Management</h1>
          <p className="text-sm text-gray-500 mt-1">SOC → On-Call → PSA → Voice escalation path</p>
        </div>
        {active.length > 0 && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
            <AlertTriangle className="w-4 h-4" />
            {active.length} Pending
          </span>
        )}
      </div>

      {/* Metrics */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <MetricCard label="Total (30d)" value={metrics.total} />
          <MetricCard label="Pending Ack" value={metrics.pending} color={metrics.pending > 0 ? 'text-red-600' : 'text-gray-900'} />
          <MetricCard label="Acknowledged" value={metrics.acknowledged} />
          <MetricCard label="Resolved" value={metrics.resolved} color="text-green-600" />
          <MetricCard
            label="Avg MTTA"
            value={metrics.avg_mtta_minutes !== null ? `${metrics.avg_mtta_minutes}m` : 'N/A'}
            sub="mean time to acknowledge"
            color={metrics.avg_mtta_minutes !== null && metrics.avg_mtta_minutes <= 5 ? 'text-green-600' : 'text-orange-500'}
          />
          <MetricCard
            label="Avg MTTR"
            value={metrics.avg_mttr_minutes !== null ? `${metrics.avg_mttr_minutes}m` : 'N/A'}
            sub="mean time to resolve"
          />
        </div>
      )}

      {/* By Level */}
      {metrics && (
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Escalations by Level (30d)</h3>
          <div className="grid grid-cols-4 gap-4">
            {(['P1', 'P2', 'P3', 'P4'] as const).map(level => {
              const LevelIcon = LEVEL_ICONS[level] ?? Bell;
              return (
                <div key={level} className={`rounded-lg p-4 ${LEVEL_COLORS[level]}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <LevelIcon className="w-4 h-4" />
                    <span className="text-sm font-semibold">{level}</span>
                  </div>
                  <p className="text-2xl font-bold">{metrics.by_level[level] ?? 0}</p>
                  <p className="text-xs mt-0.5">
                    {level === 'P1' ? 'Phone call' : level === 'P2' ? 'SMS' : level === 'P3' ? 'Slack/Email' : 'Ticket only'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Escalations */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Active Escalations</h3>
          <Clock className="w-4 h-4 text-gray-400" />
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : active.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No active escalations</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2">Level</th>
                  <th className="px-4 py-2">Incident</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Age</th>
                  <th className="px-4 py-2">PSA Ticket</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {active.map(esc => (
                  <ActiveEscalationRow key={esc.id} esc={esc} onResolve={handleResolve} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* History */}
      {metrics && metrics.history.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Recent History (30d)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-2">Level</th>
                  <th className="px-4 py-2">Incident</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">MTTA</th>
                  <th className="px-4 py-2">MTTR</th>
                  <th className="px-4 py-2">Triggered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {metrics.history.map(h => (
                  <tr key={h.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3"><Badge value={h.escalation_level} colors={LEVEL_COLORS} /></td>
                    <td className="px-4 py-3 text-gray-700 font-medium">{h.incident_title}</td>
                    <td className="px-4 py-3"><Badge value={h.status} colors={STATUS_COLORS} /></td>
                    <td className="px-4 py-3 text-gray-500">{h.mtta_minutes !== undefined ? `${h.mtta_minutes}m` : '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{h.mttr_minutes !== undefined ? `${h.mttr_minutes}m` : '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{new Date(h.triggered_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Config Panel */}
      {!configLoading && config && (
        <ConfigPanel config={config} onSave={handleSaveConfig} />
      )}

      {/* Autonomous Response Panel */}
      {!autonomousLoading && autonomousConfig && (
        <AutonomousResponsePanel config={autonomousConfig} onSave={handleSaveAutonomousConfig} />
      )}
    </div>
  );
}
